import { ApolloError, gql, useQuery } from '@apollo/client';
import { useEffect, useMemo } from 'react';
import { useAuth } from '../auth/useAuth';
import { LEARNING_GOAL_ACTIVITY_PARAMS, isRetrieveLearningGoalAssignmentsEnabled } from '../../common/constants';
import { LearningActivity, LearningGoalTranscriptsByUserIdResponse } from '../../common/types';
import { useLogger } from '@amzn/xb-hooks';
import { LEARNING_GOAL_TRANSCRIPTS_ERROR } from '../../common/rumMetrics';

export const LEARNING_GOAL_TRANSCRIPTS_BY_USER_ID = gql`
    query learningGoalTranscriptsByUserId($userId: ID! $cursor: String $pageSize: Int) {
        learningGoalTranscriptsByUserId(userId: $userId, cursor: $cursor, pageSize: $pageSize) {
            nodes {
                id
                learningGoal {
                    name
                    status
                    source
                    learningActivities {
                      ${LEARNING_GOAL_ACTIVITY_PARAMS}
                    }
                }
            }
            pageInfo {
                cursor
                size
            }
        }
    }
`;

/**
 * The learning-activity data service throws `DOWNSTREAM_SERVICE_ERROR`
 * (message "Could not find any learning activities for given query") when an assigned
 * learning goal has no activities. That is an expected "empty list" signal, not a real
 * failure, so it is filtered out of the surfaced error.
 */
const EMPTY_ACTIVITIES_ERROR_MESSAGE = 'Could not find any learning activities for given query';

/**
 * A learner's assigned learning goals are a small, bounded set, so a single generously sized page
 * covers every realistic case. We request this many in one call rather than running a pagination
 * loop; if a learner ever exceeds it (pageInfo.cursor comes back non-null), we log/count so the
 * truncation is observable instead of silent.
 */
const LEARNING_GOAL_TRANSCRIPTS_PAGE_SIZE = 200;

// Element type of ApolloError.graphQLErrors for this Apollo version (a GraphQLFormattedError,
// which exposes message/extensions — all we need — but not the full GraphQLError class).
type GraphQLErrorLike = ApolloError['graphQLErrors'][number];

// Only the true empty-goal signal — the DOWNSTREAM_SERVICE_ERROR code AND the specific
// "no activities" message — is ignored. A genuine downstream failure carrying the same code
// but a different message is still surfaced.
const isEmptyActivitiesError = (gqlError: GraphQLErrorLike): boolean =>
    gqlError.extensions?.code === 'DOWNSTREAM_SERVICE_ERROR' && (gqlError.message ?? '').includes(EMPTY_ACTIVITIES_ERROR_MESSAGE);

/**
 * Rebuilds an ApolloError from only the errors worth surfacing, dropping the expected
 * empty-learning-goal errors. Returns undefined when nothing meaningful is left.
 */
const filterIgnorableErrors = (error?: ApolloError): ApolloError | undefined => {
    if (!error) return undefined;

    const meaningfulGraphQLErrors = (error.graphQLErrors ?? []).filter((gqlError) => !isEmptyActivitiesError(gqlError));

    if (meaningfulGraphQLErrors.length === 0 && !error.networkError && !error.clientErrors?.length) {
        return undefined;
    }

    return new ApolloError({
        graphQLErrors: meaningfulGraphQLErrors,
        networkError: error.networkError,
        clientErrors: error.clientErrors,
    });
};

/**
 * Fetches the learner's assigned-learning-goal activities. Kept as its own query (separate from
 * useLearningActivitiesByUserId) so a failure on this federated path — the learningGoal ->
 * learningActivities join resolves across several subgraphs — can't fail the main learner-records
 * request. Gated by the retrieveLearningGoalAssignments flag: skipped entirely when off.
 */
export const useLearningGoalTranscriptsByUserId = () => {
    const { log, count } = useLogger();
    const { user } = useAuth();
    const enabled = isRetrieveLearningGoalAssignmentsEnabled();

    const result = useQuery<LearningGoalTranscriptsByUserIdResponse>(LEARNING_GOAL_TRANSCRIPTS_BY_USER_ID, {
        skip: !enabled || !user.id,
        variables: { userId: user.id, pageSize: LEARNING_GOAL_TRANSCRIPTS_PAGE_SIZE },
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'network-only',
        // Keep partial data when only some learning goals error out (e.g. one has no activities).
        errorPolicy: 'all',
        onError: (error) => {
            // Only log/count errors that survive the empty-learning-goal filter.
            if (filterIgnorableErrors(error)) {
                log.error('Error requesting learning goal transcripts by userId', error);
                count(LEARNING_GOAL_TRANSCRIPTS_ERROR);
            }
        },
    });

    // We intentionally fetch a single page (see LEARNING_GOAL_TRANSCRIPTS_PAGE_SIZE). A non-null
    // cursor means the learner has more transcripts than the cap, so the list would be truncated —
    // surface that instead of silently dropping activities. Run in an effect keyed on the cursor so
    // it emits once per state change, not on every render.
    const nextCursor = result.data?.learningGoalTranscriptsByUserId?.pageInfo?.cursor;
    useEffect(() => {
        if (!nextCursor) return;
        log.warn(
            `Learning goal transcripts for user exceeded the ${LEARNING_GOAL_TRANSCRIPTS_PAGE_SIZE}-transcript page cap; the dashboard list may be truncated.`,
        );
        count(LEARNING_GOAL_TRANSCRIPTS_ERROR);
    }, [nextCursor, log, count]);

    // Flatten every assigned goal's activities into a single list of LearningActivity records,
    // ready to merge into the dashboard course pipeline. Memoized so the array reference stays
    // stable across renders — consumers use it as a useEffect dependency, and a fresh array every
    // render would re-fire that effect in a loop.
    const learningGoalActivities = useMemo<LearningActivity[]>(
        () => (result.data?.learningGoalTranscriptsByUserId?.nodes ?? []).flatMap((node) => node.learningGoal.learningActivities ?? []),
        [result.data],
    );

    // Swallow the expected empty-learning-goal errors so they don't surface as a flashbar.
    // Memoized so a new ApolloError isn't constructed every render (which would also destabilize
    // consumers' effect dependencies).
    const error = useMemo(() => filterIgnorableErrors(result.error), [result.error]);

    return {
        ...result,
        learningGoalActivities,
        error,
        refetch: result.refetch,
    };
};
